import { Button, Input, Skeleton } from "@chakra-ui/react";
import { RpcError } from "grpc-web";
import { FunctionComponent, ReactElement, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import usePermission from "../../../../hooks/usePermission";
import useClaimsToast from "../../../../hooks/useToast";
import DataCardDisplay from "../../../../shared/DataDisplay/DataDisplay";
import CustomModal from "../../../../shared/Modals/CustomModal";
import { upsertUser } from "../../../../utils/api/auth";
import { UserRole } from "../../../../utils/consts";
import { isPasswordValid } from "../../../../utils/helpers";
import { handleError } from "../../../../utils/provider/claims-provider-util";
import { KVP } from "../../../../utils/types";

interface EditUserModalProps {
	user: KVP | undefined;
	isOpen: boolean;
	onClose(): void;
	onSubmit(user: KVP): void;
}

const EditUserModal: FunctionComponent<EditUserModalProps> = (props: EditUserModalProps): ReactElement => {
	const { user, isOpen, onClose, onSubmit } = props;
	const toast = useClaimsToast();
	const { roles } = usePermission();
	const navigate = useNavigate();
	const [isUpserting, setIsUpserting] = useState(false);
	const [localUser, setLocalUser] = useState({ ...user });

	const validityError: string | undefined = useMemo(() => {
		if (localUser.password && !isPasswordValid(localUser.password.toString()))
			return "Your password must be at least 8 characters long and no more than 64 characters.";
		if (!localUser.username || localUser.username == "") return "A username is required.";
		if (!localUser.email || localUser.email == "") return "A email is required.";
		if (
			localUser.email &&
			(!localUser.email.toString().includes("@") ||
				!localUser.email
					.toString()
					.split("@")
					.every((part) => part.length > 0))
		)
			return "The email is invalid.";
		if (!localUser.name || localUser.name == "") return "A name is required.";
		if (!localUser.externalId || localUser.externalId == "") return "An external ID is required.";
	}, [localUser]);

	const isUserAdmin = roles.includes(UserRole.Admin);

	const handleUpsertUser = async () => {
		setIsUpserting(true);
		try {
			// Gather only user fields that have changed
			let userChanges: KVP = { id: localUser.id };
			Object.keys(localUser).forEach((key) => {
				if (user && localUser[key] && localUser[key] != user[key]) {
					userChanges[key] = localUser[key];
				}
			});
			const response = await upsertUser(localUser).catch((err) => {
				console.error(err);
				if (typeof err == "string") {
					toast("Error", err, "error");
				} else {
					toast("Error", (err as RpcError).message, "error");
				}

				return undefined;
			});

			if (response) {
				const responseUser = response?.getUser();

				let upsertedUser: KVP = {
					...localUser,
					email: responseUser?.getEmail() ?? "",
					username: responseUser?.getUsername() ?? "",
					name: responseUser?.getName() ?? "",
					externalId: responseUser?.getExternalId() ?? ""
				};

				navigate(".", {
					replace: true,
					state: {
						userData: upsertedUser
					}
				});
				onSubmit(upsertedUser);

				toast(undefined, "User information updated successfully", "success");
			}
		} catch (error) {
			console.error("Error upserting user", error);
			handleError(toast, error as RpcError, "There was a problem updating the user", "Error updating user");
		} finally {
			setIsUpserting(false);
		}
	};

	const handleTextUpdate = (value: string, fieldId: string) => {
		setLocalUser({
			...localUser,
			[fieldId]: value
		});
	};

	return isOpen ? (
		<CustomModal
			isOpen={isOpen}
			isCentered={true}
			closeOnOverlayClick={false}
			onCancel={onClose}
			onContinue={handleUpsertUser}
			size="4xl"
			title={"Edit User " + user?.name}
			footerContent={
				<Button variant="ghost" mr="8" onClick={() => setLocalUser(user!)}>
					Reset
				</Button>
			}
			continueText="Update"
			continueDisabled={!!validityError}
			continueTooltip={validityError}
			continueLoading={isUpserting}
			content={
				<Skeleton isLoaded={!!user} endColor="gray.200">
					<DataCardDisplay
						data={[
							{
								title: "User Id",
								value: user?.id?.toString() || "N/A"
							},
							{
								title: "External Id",
								value: "",
								childElements: (
									<Input
										size="sm"
										width="290px"
										fontSize="16px"
										borderColor="#bbbbbb"
										borderRadius="3px"
										value={localUser?.externalId ?? ""}
										onChange={(event) => handleTextUpdate(event.target.value, "externalId")}
									/>
								)
							},
							...(isUserAdmin
								? [
										{
											title: "Name",
											value: "",
											childElements: (
												<Input
													size="sm"
													width="290px"
													fontSize="16px"
													borderColor="#bbbbbb"
													borderRadius="3px"
													value={localUser?.name ?? ""}
													onChange={(event) => handleTextUpdate(event.target.value, "name")}
												/>
											)
										}
									]
								: []),
							{
								title: "Username",
								value: "",
								childElements: isUserAdmin ? (
									<Input
										size="sm"
										width="290px"
										fontSize="16px"
										borderColor="#050505"
										borderRadius="3px"
										value={localUser?.username ?? ""}
										onChange={(event) => handleTextUpdate(event.target.value, "username")}
									/>
								) : (
									<>{user?.username?.toString() || "N/A"}</>
								)
							},
							{
								title: "Email",
								value: "",
								childElements: (
									<Input
										size="sm"
										width="290px"
										fontSize="16px"
										borderColor="#bbbbbb"
										borderRadius="3px"
										value={localUser?.email ?? ""}
										onChange={(event) => handleTextUpdate(event.target.value, "email")}
									/>
								)
							},
							...(isUserAdmin
								? [
										{
											title: "Password",
											value: "",
											childElements: (
												<Input
													size="sm"
													width="290px"
													fontSize="16px"
													type="password"
													borderColor="#bbbbbb"
													borderRadius="3px"
													value={localUser?.password ?? ""}
													onChange={(event) => handleTextUpdate(event.target.value, "password")}
												/>
											)
										}
									]
								: []),
							{
								title: "Roles",
								value: user?.roles?.toString() || "N/A"
							},
							...(user?.status !== undefined && user?.status !== null
								? [
										{
											title: "Status",
											value: String(user.status)
										}
									]
								: []),
							...(user?.createdAt
								? [
										{
											title: "Created At",
											value: user.createdAt.toString()
										}
									]
								: []),
							...(user?.confirmedAt
								? [
										{
											title: "Confirmed At",
											value: user.confirmedAt.toString()
										}
									]
								: [])
						]}
						leftSideRows={5}
						style={{ width: "100%" }}
						leftLabelMinWidth={100}
						rightLabelMinWidth={110}
					/>
				</Skeleton>
			}
		/>
	) : (
		<></>
	);
};

export default EditUserModal;
